import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ACCOUNT_LABELS, formatCents, PAYMENT_BEHAVIOR_LABELS, TAX_CODES, type AccountKind, type Contact, type ContactFinance as ContactFinanceData, type ContactInvoiceStats, type OpenBalance, type SupplierPurchaseStats } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { FactBoxPart, FactRow } from '../../components/CardLayout';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { InfoHint } from '../../components/InfoHint';
import { bpText, parseBp } from '../../money';
import { useSession } from '../../session';
import { accountPath } from './AccountsPage';
import { invoiceDay } from './InvoicesPage';
import './finance.css';

interface Form {
  paymentTermsDays: string;
  discountPercent: string;
  discountDays: string;
  vatId: string;
  taxCountry: string;
  reverseCharge: boolean;
  invoiceEmail: string;
  buyerReference: string;
  iban: string;
  bic: string;
  defaultTaxCode: string;
  ledgerAccount: string;
}

const toForm = (d: ContactFinanceData): Form => ({
  paymentTermsDays: d.paymentTermsDays === null ? '' : String(d.paymentTermsDays),
  discountPercent: d.discountBp ? bpText(d.discountBp) : '',
  discountDays: d.discountDays === null ? '' : String(d.discountDays),
  vatId: d.vatId ?? '',
  taxCountry: d.taxCountry ?? '',
  reverseCharge: d.reverseCharge,
  invoiceEmail: d.invoiceEmail ?? '',
  buyerReference: d.buyerReference ?? '',
  iban: d.iban ?? '',
  bic: d.bic ?? '',
  defaultTaxCode: d.defaultTaxCode ?? '',
  ledgerAccount: d.ledgerAccount ?? '',
});

/**
 * Finanzdaten auf der Kontaktkarte (KON-140): Debitor und Kreditor sind Kontakte mit Zahlungsbedingungen, USt-IdNr., Bankverbindung und Konten. Der Bereich ist
 * zugeklappt und erscheint nur, wer das Zusatzrecht „Finanzdaten“ hat. Leere Felder übernehmen die Vorgaben aus Einrichtung → Finanzen.
 */
export function ContactFinance({ contact, open, onOpenChange }: { contact: Contact; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { can } = useSession();
  if (!can('contacts.finance')) return null;
  return <Panel contact={contact} open={open} onOpenChange={onOpenChange} />;
}

/** Finanzdaten und offene Posten eines Kontakts, gemeinsam für die Infobox und die Kurzübersicht auf dem Handy */
function useFinanceSummary(contact: Contact) {
  const { can, hasModule } = useSession();
  const data = useQuery({ queryKey: ['contact-finance', contact.id], queryFn: async () => (await api<{ data: ContactFinanceData }>('GET', `/contacts/${contact.id}/finance`)).data, enabled: can('contacts.finance') });
  const balance = useQuery({
    queryKey: ['open-balance', contact.id],
    queryFn: async () => (await api<{ data: OpenBalance }>('GET', `/invoices-out/open-balance?contactId=${contact.id}`)).data,
    enabled: hasModule('invoices_out') && can('invoices_out.read') && can('invoices_out.read_amounts'),
  });
  const stats = useQuery({
    queryKey: ['contact-stats', contact.id],
    queryFn: async () => (await api<{ data: ContactInvoiceStats }>('GET', `/invoices-out/contact-stats?contactId=${contact.id}`)).data,
    enabled: hasModule('invoices_out') && can('invoices_out.read') && can('invoices_out.read_amounts'),
  });
  return { data: data.data, loading: data.isLoading, balance: balance.data, stats: stats.data };
}

/** Zahlungsziel und Skonto so, wie sie für diesen Kontakt tatsächlich gelten, mit Hinweis, wenn sie aus der Einrichtung stammen */
function termsText(d: ContactFinanceData): { terms: string; discount: string } {
  const e = d.effective;
  return {
    terms: `${e.paymentTermsDays} Tage${e.paymentTermsFrom === 'setup' ? ' (Vorgabe)' : ''}`,
    discount: e.discountBp ? `${bpText(e.discountBp)} % bei Zahlung in ${e.discountDays} Tagen${e.discountFrom === 'setup' ? ' (Vorgabe)' : ''}` : 'kein Skonto',
  };
}

const behaviorClass = (b: ContactInvoiceStats['behavior']) => `finance-behavior finance-behavior--${b}`;

const balanceText = (b: OpenBalance) => (b.count === 0 ? 'keine' : `${formatCents(b.openCents)} (${b.count})`);

/**
 * Finanzdaten in der Infobox neben dem Kontakt (Desktop): Debitorennummer, offene Posten, Zahlungsbedingungen, Steuer und Bank auf einen Blick.
 * Bearbeitet wird in der Karte „Finanzdaten bearbeiten“, die der Knopf öffnet. Auf dem Handy ersetzt die Kurzübersicht (ContactFinanceGlance) diese Infobox.
 */
export function ContactFinanceFactBox({ contact, onEdit }: { contact: Contact; onEdit: () => void }) {
  const { can } = useSession();
  const { data, loading, balance, stats } = useFinanceSummary(contact);
  if (!can('contacts.finance')) return null;
  const t = data ? termsText(data) : null;
  return (
    <div className="finance-factbox">
      {stats && (
        <FactBoxPart title="Umsatz und Zahlung" storageKey="contact-stats-box">
          <FactRow label="Umsatz diesen Monat">{formatCents(stats.revenueMonthCents)}</FactRow>
          <FactRow label="Umsatz dieses Jahr">{formatCents(stats.revenueYearCents)}</FactRow>
          <FactRow label="Umsatz gesamt"><strong>{formatCents(stats.revenueTotalCents)}</strong></FactRow>
          <FactRow label="Rechnungen">{stats.invoiceCount === 0 ? null : `${stats.invoiceCount}, letzte vom ${invoiceDay(stats.lastInvoiceDate)}`}</FactRow>
          <FactRow label="Offene Posten">{stats.openCents === 0 ? 'keine' : <Link to={`/rechnungen?payment=open&q=${encodeURIComponent(contact.name)}`}>{formatCents(stats.openCents)}</Link>}</FactRow>
          {stats.overdueCount > 0 && <FactRow label="Davon überfällig"><span className="finance-balance__late">{stats.overdueCount} {stats.overdueCount === 1 ? 'Rechnung' : 'Rechnungen'}{balance ? `, ${formatCents(balance.overdueCents)}` : ''}</span></FactRow>}
          <FactRow label="Ø Zahlungsdauer">{stats.avgPaymentDays === null ? null : `${stats.avgPaymentDays} Tage`}</FactRow>
          <FactRow label="Pünktlich bezahlt">{stats.onTimeRate === null ? null : `${stats.onTimeRate} %`}</FactRow>
          <FactRow label="Zahlungsverhalten">
            <span className={behaviorClass(stats.behavior)}>{PAYMENT_BEHAVIOR_LABELS[stats.behavior]}</span>
            <InfoHint label="Zahlungsverhalten">Wirkia schätzt das aus Ihren eigenen Rechnungen an diesen Kunden: wie schnell er bezahlt und wie oft er die Frist einhält. Es ist keine Bonitätsauskunft einer Auskunftei und sagt nichts über Rechnungen anderer Firmen. Erst ab zwei bezahlten Rechnungen oder einer überfälligen gibt es eine Einschätzung.</InfoHint>
          </FactRow>
        </FactBoxPart>
      )}

      <FactBoxPart title="Finanzdaten" storageKey="contact-finance-box">
        {loading && <p className="muted small">Wird geladen …</p>}
        {data && t && (
          <>
            <FactRow label="Debitorennummer">{data.debtorNumber ? <Link to={`${accountPath('debtor')}/${data.debtorId}`}><strong>{data.debtorNumber}</strong></Link> : null}</FactRow>
            {data.creditorNumber && <FactRow label="Kreditorennummer"><Link to={`${accountPath('creditor')}/${data.creditorId}`}><strong>{data.creditorNumber}</strong></Link></FactRow>}
            <FactRow label="Zahlungsziel">{t.terms}</FactRow>
            <FactRow label="Skonto">{t.discount}</FactRow>
            <FactRow label="USt-IdNr.">{data.vatId}</FactRow>
            {data.reverseCharge && <FactRow label="§ 13b UStG">Steuerschuld beim Kunden</FactRow>}
            <FactRow label="E-Mail für Rechnungen">{data.invoiceEmail}</FactRow>
            <FactRow label="IBAN">{data.iban}</FactRow>
            <p className="finance-factbox__action"><button type="button" className="btn btn--secondary btn--sm" onClick={onEdit}>Bearbeiten …</button></p>
          </>
        )}
      </FactBoxPart>
    </div>
  );
}

/**
 * Infobox am Kreditor (dem Lieferanten): was der Betrieb dort einkauft, was offen und bald fällig ist, wie pünktlich er selbst zahlt und wie viel Skonto er genutzt hat.
 * Darunter die Finanzdaten wie beim Debitor. Die Zahlen stammen aus den freigegebenen Eingangsrechnungen (GET /invoices-in/supplier-stats).
 */
export function CreditorFactBox({ contact, onEdit }: { contact: Contact; onEdit: () => void }) {
  const { can, hasModule } = useSession();
  const data = useQuery({ queryKey: ['contact-finance', contact.id], queryFn: async () => (await api<{ data: ContactFinanceData }>('GET', `/contacts/${contact.id}/finance`)).data, enabled: can('contacts.finance') });
  const stats = useQuery({
    queryKey: ['supplier-stats', contact.id],
    queryFn: async () => (await api<{ data: SupplierPurchaseStats }>('GET', `/invoices-in/supplier-stats?supplierId=${contact.id}`)).data,
    enabled: hasModule('invoices_in') && can('invoices_in.read'),
  });
  if (!can('contacts.finance')) return null;
  const s = stats.data;
  const t = data.data ? termsText(data.data) : null;
  const list = (filter: string) => `/eingangsrechnungen?${filter}&q=${encodeURIComponent(contact.name)}`;
  return (
    <div className="finance-factbox">
      {s && (
        <FactBoxPart title="Einkauf und Zahlung" storageKey="creditor-stats-box">
          <FactRow label="Einkauf diesen Monat">{formatCents(s.spendMonthCents)}</FactRow>
          <FactRow label="Einkauf dieses Jahr">{formatCents(s.spendYearCents)}</FactRow>
          <FactRow label="Einkauf gesamt"><strong>{formatCents(s.spendTotalCents)}</strong></FactRow>
          <FactRow label="Rechnungen">{s.invoiceCount === 0 ? null : `${s.invoiceCount}, letzte vom ${invoiceDay(s.lastInvoiceDate)}`}</FactRow>
          <FactRow label="Offen">{s.openCount === 0 ? 'nichts' : <Link to={list('payment=open')}>{formatCents(s.openCents)} ({s.openCount})</Link>}</FactRow>
          {s.overdueCount > 0 && <FactRow label="Davon überfällig"><span className="finance-balance__late">{formatCents(s.overdueCents)} ({s.overdueCount})</span></FactRow>}
          {s.nextDueDate && <FactRow label="Nächste Fälligkeit">{invoiceDay(s.nextDueDate)}{s.nextDueCents !== null ? `, ${formatCents(s.nextDueCents)}` : ''}</FactRow>}
          {s.draftCount > 0 && <FactRow label="Zu prüfen"><Link to={list('status=draft')}>{s.draftCount} {s.draftCount === 1 ? 'Entwurf' : 'Entwürfe'}</Link></FactRow>}
          <FactRow label="Ø eigene Zahlungsdauer">{s.avgPaymentDays === null ? null : `${s.avgPaymentDays} Tage`}</FactRow>
          <FactRow label="Pünktlich bezahlt">{s.onTimeRate === null ? null : `${s.onTimeRate} %`}</FactRow>
          <FactRow label="Skonto genutzt">{s.discountCents > 0 ? formatCents(s.discountCents) : null}</FactRow>
        </FactBoxPart>
      )}

      <FactBoxPart title="Finanzdaten" storageKey="creditor-finance-box">
        {data.isLoading && <p className="muted small">Wird geladen …</p>}
        {data.data && t && (
          <>
            <FactRow label="Kreditorennummer">{data.data.creditorNumber ? <strong>{data.data.creditorNumber}</strong> : null}</FactRow>
            {data.data.debtorNumber && <FactRow label="Debitorennummer"><Link to={`${accountPath('debtor')}/${data.data.debtorId}`}><strong>{data.data.debtorNumber}</strong></Link></FactRow>}
            <FactRow label="Zahlungsziel">{t.terms}</FactRow>
            <FactRow label="Skonto">{t.discount}</FactRow>
            <FactRow label="USt-IdNr.">{data.data.vatId}</FactRow>
            <FactRow label="IBAN">{data.data.iban}</FactRow>
            <FactRow label="Personenkonto">{data.data.ledgerAccount}</FactRow>
            <p className="finance-factbox__action"><button type="button" className="btn btn--secondary btn--sm" onClick={onEdit}>Bearbeiten …</button></p>
          </>
        )}
      </FactBoxPart>
    </div>
  );
}

/**
 * Kurzübersicht für Handy und Tablet, wo die Infobox unter der ganzen Karte stünde: drei Kennzahlen direkt unter den Kontaktdaten und ein Knopf zu allen Angaben.
 */
export function ContactFinanceGlance({ contact, onEdit }: { contact: Contact; onEdit: () => void }) {
  const { can } = useSession();
  const { data, balance, stats } = useFinanceSummary(contact);
  if (!can('contacts.finance') || !data) return null;
  return (
    <section className="card finance-glance" aria-label="Finanzen auf einen Blick">
      <div className="factbox__tiles finance-glance__tiles">
        <div className="factbox__tile"><strong>{data.debtorNumber ?? '–'}</strong><span>Debitorennummer</span></div>
        <div className="factbox__tile"><strong>{balance ? (balance.count === 0 ? '0,00 €' : formatCents(balance.openCents)) : '–'}</strong><span>Offene Posten{balance && balance.count > 0 ? ` (${balance.count})` : ''}</span></div>
        {stats && <div className="factbox__tile"><strong>{formatCents(stats.revenueYearCents)}</strong><span>Umsatz dieses Jahr</span></div>}
        {stats && <div className="factbox__tile"><strong className={behaviorClass(stats.behavior)}>{PAYMENT_BEHAVIOR_LABELS[stats.behavior]}</strong><span>Zahlungsverhalten</span></div>}
        {balance && balance.overdueCount > 0 && <div className="factbox__tile finance-glance__late"><strong>{formatCents(balance.overdueCents)}</strong><span>Überfällig ({balance.overdueCount})</span></div>}
      </div>
      <p className="muted small">Zahlungsziel {termsText(data).terms}, Skonto: {termsText(data).discount}.</p>
      <div className="finance-glance__actions">
        {balance && balance.count > 0 && <Link className="btn btn--secondary btn--sm" to={`/rechnungen?payment=open&q=${encodeURIComponent(contact.name)}`}>Rechnungen ansehen</Link>}
        <button type="button" className="btn btn--secondary btn--sm" onClick={onEdit}>Finanzdaten …</button>
      </div>
    </section>
  );
}

/**
 * Die Finanzdaten eines Kontakts als eigene Karte ohne Zuklappen, für die Masken der Debitoren und Kreditoren (KON-161). `scope` blendet aus, was zur anderen Art
 * gehört: Rechnungsversand und Leitweg-ID gibt es nur beim Kunden.
 */
export function ContactFinanceEditor({ contact, scope }: { contact: Contact; scope: AccountKind }) {
  return <Panel contact={contact} open onOpenChange={() => undefined} embedded scope={scope} />;
}

function Shell({ embedded, open, onOpenChange, children }: { embedded: boolean; open: boolean; onOpenChange: (open: boolean) => void; children: React.ReactNode }) {
  if (embedded) {
    return (
      <section className="card finance-edit" aria-label="Finanzdaten">
        <h2>Finanzdaten</h2>
        {children}
      </section>
    );
  }
  return <CollapsibleCard title="Finanzdaten bearbeiten" ariaLabel="Finanzdaten" open={open} onOpenChange={onOpenChange} className="finance-edit">{children}</CollapsibleCard>;
}

function Panel({ contact, open, onOpenChange, embedded = false, scope }: { contact: Contact; open: boolean; onOpenChange: (open: boolean) => void; embedded?: boolean; scope?: AccountKind }) {
  const { can, hasModule } = useSession();
  const canWrite = can('contacts.finance');
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['contact-finance', contact.id], queryFn: async () => (await api<{ data: ContactFinanceData }>('GET', `/contacts/${contact.id}/finance`)).data });
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    if (!query.data || loaded.current === query.data.updatedAt + contact.id) return;
    loaded.current = query.data.updatedAt + contact.id;
    setForm(toForm(query.data));
  }, [query.data, contact.id]);

  const store = (data: ContactFinanceData) => client.setQueryData(['contact-finance', contact.id], data);
  const save = useMutation({
    mutationFn: async (f: Form) => {
      const percent = f.discountPercent.trim() === '' ? null : parseBp(f.discountPercent);
      if (f.discountPercent.trim() !== '' && percent === null) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { discountBp: 'Bitte eine Zahl angeben, zum Beispiel 2 oder 1,5' });
      const number = (text: string) => (text.trim() === '' ? null : Number(text.trim()));
      return (
        await api<{ data: ContactFinanceData }>('PUT', `/contacts/${contact.id}/finance`, {
          paymentTermsDays: number(f.paymentTermsDays),
          discountBp: percent,
          discountDays: number(f.discountDays),
          vatId: f.vatId.trim(),
          taxCountry: f.taxCountry.trim(),
          reverseCharge: f.reverseCharge,
          invoiceEmail: f.invoiceEmail.trim(),
          buyerReference: f.buyerReference.trim(),
          iban: f.iban.trim(),
          bic: f.bic.trim(),
          defaultTaxCode: f.defaultTaxCode,
          ledgerAccount: f.ledgerAccount.trim(),
        })
      ).data;
    },
    onSuccess: (d) => {
      store(d);
      setErrors({});
      setNotice({ ok: true, text: 'Die Finanzdaten sind gespeichert.' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setNotice({ ok: false, text: 'Bitte prüfen Sie die markierten Felder.' });
      } else setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Die Finanzdaten konnten nicht gespeichert werden.' });
    },
  });
  const assign = useMutation({
    mutationFn: async (kind: 'debtor' | 'creditor') => (await api<{ data: ContactFinanceData }>('POST', `/contacts/${contact.id}/finance/number`, { kind })).data,
    onSuccess: (d) => {
      store(d);
      client.invalidateQueries({ queryKey: ['accounts'] });
      setNotice(null);
    },
    onError: (e) => setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Das Konto konnte nicht angelegt werden.' }),
  });

  const data = query.data;
  const set = <K extends keyof Form>(key: K, value: Form[K]) => form && setForm({ ...form, [key]: value });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  function submit(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    if (form) save.mutate(form);
  }

  /** Das Konto als Debitor oder Kreditor: Nummer mit Link zur Maske, oder der Knopf, der das Konto anlegt (nur mit dem Modul der jeweiligen Art) */
  const numberRow = (kind: AccountKind, value: string | null, id: string | null, hint: string) => {
    const names = ACCOUNT_LABELS[kind];
    const licensed = hasModule(kind === 'debtor' ? 'invoices_out' : 'invoices_in');
    return (
      <div className="finance-number">
        <span className="finance-number__label">{names.number} ({names.person})<InfoHint label={names.number}>{hint}</InfoHint></span>
        {value ? (
          <span><strong>{value}</strong>{id && licensed ? <> · <Link to={`${accountPath(kind)}/${id}`}>Maske öffnen</Link></> : null}</span>
        ) : canWrite && licensed ? (
          <button type="button" className="btn btn--secondary btn--sm" disabled={assign.isPending} onClick={() => assign.mutate(kind)}>Als {names.singular} anlegen</button>
        ) : (
          <span className="muted">–</span>
        )}
      </div>
    );
  };

  return (
    <Shell embedded={embedded} open={open} onOpenChange={onOpenChange}>
      {query.isLoading && <p className="muted">Wird geladen …</p>}
      {query.isError && <div className="alert alert--error" role="alert">{(query.error as Error).message}</div>}
      {data && form && (
        <form onSubmit={submit} noValidate>
          <p className="muted small">Hier steht, wie {contact.name} abgerechnet wird. Leere Felder übernehmen die Vorgaben aus Einrichtung → Finanzen. Nummern und Konten stehen später in Rechnungen und im DATEV-Export.</p>
          {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role="status">{notice.text}</div>}

          {!embedded && (
            <div className="finance-numbers">
              {numberRow('debtor', data.debtorNumber, data.debtorId, 'Die Nummer, unter der dieser Kontakt als Kunde in der Buchhaltung geführt wird. Debitoren haben eine eigene Liste und Maske. Die Nummer wird nur einmal vergeben und ändert sich nie.')}
              {numberRow('creditor', data.creditorNumber, data.creditorId, 'Die Nummer, unter der dieser Kontakt als Lieferant in der Buchhaltung geführt wird. Kreditoren haben eine eigene Liste und Maske. Die Nummer wird nur einmal vergeben und ändert sich nie.')}
            </div>
          )}

          <section className="finance-section">
          <h3>Zahlungsbedingungen</h3>
          <div className="finance-grid">
            <label className="field">
              <span>Zahlungsziel in Tagen<InfoHint label="Zahlungsziel">Gilt für diesen Kontakt statt der Vorgabe aus der Einrichtung. In jedem Angebot, Auftrag und jeder Rechnung lässt es sich noch ändern.</InfoHint></span>
              <input className="input num" inputMode="numeric" placeholder="wie in der Einrichtung" value={form.paymentTermsDays} onChange={(e) => set('paymentTermsDays', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.paymentTermsDays} />
              {err('paymentTermsDays')}
            </label>
            <label className="field">
              <span>Skonto in Prozent<InfoHint label="Skonto">Preisnachlass bei schneller Zahlung, zum Beispiel 2 % bei Zahlung innerhalb von 10 Tagen. Leer: Vorgabe aus der Einrichtung.</InfoHint></span>
              <input className="input num" inputMode="decimal" placeholder="wie in der Einrichtung" value={form.discountPercent} onChange={(e) => set('discountPercent', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.discountBp} />
              {err('discountBp')}
            </label>
            <label className="field">
              <span>Skonto, wenn gezahlt innerhalb von (Tagen)</span>
              <input className="input num" inputMode="numeric" placeholder="wie in der Einrichtung" value={form.discountDays} onChange={(e) => set('discountDays', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.discountDays} />
              {err('discountDays')}
            </label>
          </div>

          </section>

          <section className="finance-section">
          <h3>Steuer und Rechnungsversand</h3>
          <div className="finance-grid">
            <label className="field">
              <span>USt-IdNr.</span>
              <input className="input" value={form.vatId} placeholder="DE123456789" onChange={(e) => set('vatId', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.vatId} />
              {err('vatId')}
            </label>
            <label className="field">
              <span>Steuerland</span>
              <input className="input" value={form.taxCountry} placeholder="DE" maxLength={2} onChange={(e) => set('taxCountry', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.taxCountry} />
              {err('taxCountry')}
            </label>
            <label className="field">
              <span>Standard-Steuerschlüssel<InfoHint label="Standard-Steuerschlüssel">Welcher Steuerschlüssel bei Rechnungen an diesen Kontakt vorgeschlagen wird. Leer: der Satz der Position.</InfoHint></span>
              <select className="input" value={form.defaultTaxCode} onChange={(e) => set('defaultTaxCode', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.defaultTaxCode}>
                <option value="">Wie in der Position</option>
                {TAX_CODES.map((c) => <option key={c.code} value={c.code}>{c.code}: {c.label}</option>)}
              </select>
              {err('defaultTaxCode')}
            </label>
            {scope !== 'creditor' && (
            <label className="field">
              <span>E-Mail für Rechnungen</span>
              <input className="input" type="email" value={form.invoiceEmail} placeholder={contact.email ?? ''} onChange={(e) => set('invoiceEmail', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.invoiceEmail} />
              {err('invoiceEmail')}
            </label>
            )}
            {scope !== 'creditor' && (
            <label className="field">
              <span>Leitweg-ID oder Bestellreferenz<InfoHint label="Leitweg-ID">Behörden und manche Konzerne verlangen eine Kennung auf der Rechnung, zum Beispiel die Leitweg-ID. Sie wird in die E-Rechnung übernommen.</InfoHint></span>
              <input className="input" value={form.buyerReference} onChange={(e) => set('buyerReference', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.buyerReference} />
              {err('buyerReference')}
            </label>
            )}
          </div>
          <label className="check finance-check">
            <input type="checkbox" checked={form.reverseCharge} onChange={(e) => set('reverseCharge', e.target.checked)} disabled={!canWrite} />
            <span>Steuerschuld beim Leistungsempfänger (§ 13b UStG)<InfoHint label="Steuerschuld beim Leistungsempfänger">Bei bestimmten Leistungen an Unternehmen schuldet der Kunde die Umsatzsteuer, nicht Sie, zum Beispiel bei Bauleistungen oder Lieferungen ins EU-Ausland. Die Rechnung weist dann keine Steuer aus und trägt einen Hinweis. Fragen Sie im Zweifel Ihren Steuerberater.</InfoHint></span>
          </label>

          </section>

          <section className="finance-section">
          <h3>Bank und Buchhaltung</h3>
          <div className="finance-grid">
            <label className="field">
              <span>IBAN</span>
              <input className="input" value={form.iban} placeholder="DE89 3704 0044 0532 0130 00" onChange={(e) => set('iban', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.iban} />
              {err('iban')}
            </label>
            <label className="field">
              <span>BIC</span>
              <input className="input" value={form.bic} placeholder="COBADEFFXXX" maxLength={11} onChange={(e) => set('bic', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.bic} />
              {err('bic')}
            </label>
            <label className="field">
              <span>Personenkonto<InfoHint label="Personenkonto">Das Konto in der Buchhaltung für diesen Kunden oder Lieferanten, meist gleich der Debitoren- oder Kreditorennummer. Leer: Wirkia nimmt die vergebene Nummer.</InfoHint></span>
              <input className="input num" inputMode="numeric" maxLength={8} value={form.ledgerAccount} onChange={(e) => set('ledgerAccount', e.target.value)} disabled={!canWrite} aria-invalid={!!errors.ledgerAccount} />
              {err('ledgerAccount')}
            </label>
          </div>
          </section>

          {canWrite && (
            <div className="form-actions">
              <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
            </div>
          )}
        </form>
      )}
    </Shell>
  );
}
